import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 max-w-[900px] mx-auto px-4 md:px-6 text-center py-20">
          <p className="text-foreground-500 text-lg">文章未找到</p>
          <Link to="/" className="text-primary-400 hover:text-primary-300 mt-4 inline-block whitespace-nowrap">
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '窝窝影院',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://wowocinema.com/article/${article.id}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 max-w-[900px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '影视资讯', href: '/category/all' },
              { label: article.title },
            ]}
          />

          <article>
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs text-accent-400 bg-accent-100/20 px-3 py-1 rounded-full whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-sm text-foreground-500">{article.date}</span>
                <span className="text-sm text-foreground-600">
                  <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                    <i className="ri-user-line"></i>
                  </div>
                  {article.author}
                </span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-100 leading-snug mb-4">
                {article.title}
              </h1>
            </header>

            <div className="w-full aspect-[2/1] rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-invert max-w-none text-foreground-300 leading-relaxed text-base"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </article>

          {relatedArticles.length > 0 && (
            <section className="mt-16 pb-12">
              <h3 className="font-heading text-xl text-foreground-100 mb-6">相关文章</h3>
              <div className="grid grid-cols-1 gap-4">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}